// src/components/inventory/TransferTabs.js
//
// #757, Session 79.22b.5.5c -- THE TRANSFERS TABS AS BINDER DIVIDERS, as Colby
// approved them (R1 at 79.22b.5.5c, "approve as drawn, bar breaks"; R2-R4 at
// 79.22b.5.5b). The drawings:
//   docs/audits/79.22b.5.5c/APPROVED_DRAWING_757_DESK.html (desk and tablet)
//   docs/audits/79.22b.5.5b/APPROVED_DRAWING_757_PHONE.html (the phone, M4)
// The rules these draw from are in src/lib/transferTabs.js, where a test holds
// them.
//
// Desk and tablet: paper tabs in a row, each in its stage's pale colour with
// the stage colour along its top; the chosen one white and joined to the sheet
// below it. The stage-coloured bar along the sheet's top edge BREAKS under the
// chosen tab (R1), because the bar is the strip's own bottom edge and the tab
// reaches down over it. Under the sheet, the edges of the sheets behind it.
//
// Phone: one row (M4) - the chosen stage's tab, and the other six as small paper
// edges carrying their counts. The edges are too narrow to be buttons on their
// own (40px with a finger), so the whole row is one button that opens the list
// of all seven: choosing a stage is two taps, as it was.

import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import {
  TAB_BAR, tabCountPill, stripArrows, phoneEdges, stageRowLabel, MATCH_PAIR,
  SHEET_BORDER, SHEET_PAD, SHEET_PAD_PHONE, SHEET_PILE, SHEET_SHADOW_PHONE, HEADER_LINE,
} from '../../lib/transferTabs';

const SHEET_LINE = '#D6D6D6';

// THE LINE UNDER THE PAGE HEADER -- Colby: "a header line that divides the
// header from the page like other pages have". Grey (R4); the stage colour is
// the sheet's.
export function HeaderLine({ margin }) {
  return <div aria-hidden="true" data-header-line style={{ height: 1, background: HEADER_LINE, margin }} />;
}

export function CountPill({ pill, n }) {
  return (
    <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 999, padding: '1px 6px', background: pill.bg, color: pill.fg }}>
      {n}
    </span>
  );
}

// THE SHEET the chosen tab is joined to. On a desk it is the page's remaining
// height (`fill`), and the list and the chosen transfer sit inside it.
export function TabSheet({ phone = false, fill = false, children }) {
  const edge = `${SHEET_BORDER}px solid ${SHEET_LINE}`;
  return (
    <div data-tab-sheet style={{
      background: '#FFFFFF',
      borderLeft: edge, borderRight: edge, borderBottom: edge,
      // On a phone the sheet has its own top edge, which the chosen tab covers
      // by a pixel (M4). On a desk and a tablet its top edge is the coloured bar
      // at the foot of the tab strip.
      borderTop: phone ? edge : 'none',
      borderRadius: phone ? '0 10px 10px 10px' : '0 0 10px 10px',
      padding: phone ? SHEET_PAD_PHONE : SHEET_PAD,
      boxShadow: phone ? SHEET_SHADOW_PHONE : SHEET_PILE,
      boxSizing: 'border-box',
      ...(fill ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : null),
    }}>
      {children}
    </div>
  );
}

// THE ROW OF PAPER TABS (desk and tablet). The tabs are this element's own
// children, so the page's opening-tab centring (`btn.parentElement`) still
// finds the row it scrolls. #700's rule holds: the wrapper fills the column
// without ever asking it to grow, and the tabs scroll inside it.
export function PaperTabStrip({ color, coarse, chosenId = null, children }) {
  const ref = useRef(null);
  // THE CHOSEN TAB IS ALWAYS IN SIGHT. A link that opens Transfers on Finished
  // at a narrow width would otherwise leave the chosen tab past the row's end,
  // under an arrow. The row's own scroll, never scrollIntoView(), which moves
  // the whole page sideways (#700, found on screen at 79.22b.4.4).
  const keepInView = useCallback(() => {
    const s = ref.current;
    const b = s && s.querySelector('[aria-pressed="true"]');
    if (!b || s.scrollWidth <= s.clientWidth + 1) return;
    const sb = s.getBoundingClientRect();
    const bb = b.getBoundingClientRect();
    const room = 44;   // an arrow's width
    if (bb.left < sb.left + room) s.scrollLeft -= (sb.left + room) - bb.left;
    else if (bb.right > sb.right - room) s.scrollLeft += bb.right - (sb.right - room);
  }, []);
  useEffect(() => { keepInView(); }, [chosenId, keepInView]);
  // The counts arrive after the first paint and widen the tabs, so the row is
  // checked again whenever its contents change width - and only then, so a
  // person scrolling the row is never pulled back.
  const lastWidth = useRef(0);
  const [ends, setEnds] = useState({ left: false, right: false });
  const sync = useCallback(() => {
    const s = ref.current;
    if (!s) return;
    const next = stripArrows({ scrollLeft: s.scrollLeft, clientWidth: s.clientWidth, scrollWidth: s.scrollWidth });
    setEnds(prev => (prev.left === next.left && prev.right === next.right ? prev : next));
  }, []);
  // Grok's review, 79.22b.5.5c -- AND WHEN THE ROW ITSELF NARROWS. Opening the
  // left-hand menu, resizing the window or turning a tablet changes the row's
  // width, which would slide a chosen tab near the end out of sight; a finger
  // would have to swipe to find the stage it is already on.
  const lastRow = useRef(0);
  useEffect(() => {
    const s = ref.current;
    const onResize = () => {
      if (s && s.clientWidth !== lastRow.current) {
        lastRow.current = s.clientWidth;
        keepInView();
      }
      sync();
    };
    window.addEventListener('resize', onResize);
    const ro = s && typeof ResizeObserver === 'function' ? new ResizeObserver(onResize) : null;
    if (ro) ro.observe(s);
    return () => {
      window.removeEventListener('resize', onResize);
      if (ro) ro.disconnect();
    };
  }, [sync, keepInView]);
  // A count or a search changes the tabs' widths without resizing the row.
  useEffect(() => {
    const s = ref.current;
    if (s && s.scrollWidth !== lastWidth.current) {
      lastWidth.current = s.scrollWidth;
      keepInView();
    }
    sync();
  });

  const step = dir => {
    const s = ref.current;
    if (!s) return;
    const reduce = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const left = dir * Math.round(s.clientWidth * 0.6);
    if (typeof s.scrollBy === 'function') s.scrollBy({ left, behavior: reduce ? 'auto' : 'smooth' });
    else s.scrollLeft += left;
  };

  return (
    <div style={{ position: 'relative', width: 0, minWidth: '100%' }}>
      <div
        ref={ref}
        data-transfer-strip
        className="transfer-paper-tabs"
        onScroll={sync}
        style={{
          display: 'flex', alignItems: 'flex-end', gap: 4, overflowX: 'auto', overflowY: 'hidden',
          scrollbarWidth: 'none', padding: '0 10px', boxSizing: 'border-box', width: '100%',
          background: `linear-gradient(${color}, ${color}) 0 100% / 100% ${TAB_BAR}px no-repeat`,
        }}
      >
        {children}
      </div>
      {/* A FINGER SWIPES; A MOUSE CANNOT. Only where there is more to see. */}
      {!coarse && ends.left && <StripArrow dir={-1} onPress={() => step(-1)} />}
      {!coarse && ends.right && <StripArrow dir={1} onPress={() => step(1)} />}
    </div>
  );
}

function StripArrow({ dir, onPress }) {
  const left = dir < 0;
  return (
    <button
      type="button"
      onClick={onPress}
      aria-label={left ? 'Earlier stages' : 'More stages'}
      data-strip-arrow={left ? 'left' : 'right'}
      style={{
        position: 'absolute', top: 0, bottom: TAB_BAR, [left ? 'left' : 'right']: 0, zIndex: 3,
        width: 44, padding: 0, border: 'none', borderRadius: 0, cursor: 'pointer', font: 'inherit',
        display: 'flex', alignItems: 'center', justifyContent: left ? 'flex-start' : 'flex-end',
        background: `linear-gradient(${left ? 90 : 270}deg, var(--off-white) 55%, rgba(247,247,247,0))`,
      }}
    >
      <span aria-hidden="true" style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28,
        borderRadius: '50%', background: '#FFFFFF', border: '1px solid #CFCFCF',
        boxShadow: '0 1px 3px rgba(0,0,0,0.15)', fontSize: 18, lineHeight: 1, color: '#3A3A3A',
      }}>
        {left ? '‹' : '›'}
      </span>
    </button>
  );
}

// THE PHONE ROW -- M4. `tabs` is the page's TRANSFER_TABS (passed in rather than
// imported, so this file does not import the page).
export function PhoneStageRow({ tabs, tab, onChoose, counts = {}, searching = false }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const rowRef = useRef(null);
  const listRef = useRef(null);
  const chosen = tabs.find(t => t.id === tab) || tabs[0];
  const count = counts[chosen.id] || 0;
  const chosenPill = tabCountPill({ count, on: true, searching, ink: chosen.ink });

  const listId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const focusInside = () => !!(wrapRef.current && wrapRef.current.contains(document.activeElement));
    // A tap anywhere else closes the list, and so does Escape, handing the
    // focus back to the row it opened from.
    // Grok's review, 79.22b.5.5c -- the focus moves to the row BEFORE the list
    // goes, wherever the tap lands: a focused choice that simply disappears
    // drops the keyboard's place to the top of the page. And Escape moves it
    // only from inside the list, never pulling a person back who has left it.
    const onDown = e => {
      if (!wrapRef.current || wrapRef.current.contains(e.target)) return;
      if (focusInside() && rowRef.current) rowRef.current.focus();
      setOpen(false);
    };
    const onKey = e => {
      if (e.key !== 'Escape' || !focusInside()) return;
      setOpen(false);
      if (rowRef.current) rowRef.current.focus();
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    const sel = listRef.current && listRef.current.querySelector('[aria-selected="true"]');
    if (sel) sel.focus();
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const onListKey = e => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const opts = Array.from(listRef.current.querySelectorAll('[role="option"]'));
    const i = opts.indexOf(document.activeElement);
    const next = opts[(i + (e.key === 'ArrowDown' ? 1 : -1) + opts.length) % opts.length];
    if (next) next.focus();
  };

  const choose = id => {
    onChoose(id);
    setOpen(false);
    if (rowRef.current) rowRef.current.focus();
  };

  return (
    <div
      ref={wrapRef}
      // Tabbing out of the open list closes it, leaving the focus where the
      // person sent it (Grok's review).
      onBlur={e => { if (open && e.relatedTarget && !wrapRef.current.contains(e.relatedTarget)) setOpen(false); }}
      style={{ position: 'relative', zIndex: open ? 50 : 2 }}
    >
      <button
        ref={rowRef}
        type="button"
        data-stage-row
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={stageRowLabel(chosen.label, count)}
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'flex-end', gap: 5, width: '100%', minHeight: 44,
          padding: 0, margin: '0 0 -1px', border: 'none', background: 'transparent',
          cursor: 'pointer', position: 'relative', font: 'inherit', textAlign: 'left',
        }}
      >
        {/* The chosen stage. 14px rather than the drawing's 15, so the longest
            name fits a 375 phone with room to spare (#757's amendment). */}
        <span style={{
          boxSizing: 'border-box', display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0,
          minHeight: 44, padding: '0 9px', background: '#FFFFFF', color: chosen.ink,
          fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap',
          borderLeft: `1px solid ${SHEET_LINE}`, borderRight: `1px solid ${SHEET_LINE}`,
          borderTop: `4px solid ${chosen.color}`, borderBottom: 'none',
          borderRadius: '10px 10px 0 0', boxShadow: '0 -2px 6px rgba(0,0,0,0.08)',
          position: 'relative', zIndex: 9,
        }}>
          {chosen.label}
          {chosenPill && <CountPill pill={chosenPill} n={count} />}
          <span aria-hidden="true" style={{ fontSize: 10, color: '#5C5C5C' }}>{open ? '▲' : '▼'}</span>
        </span>
        <span style={{ display: 'flex', flex: 1, alignItems: 'flex-end', minWidth: 0 }}>
          {phoneEdges(tabs, chosen.id).map((e, j) => {
            const n = counts[e.id] || 0;
            const matched = searching && n > 0;
            return (
              <span key={e.id} data-stage-edge={e.id} title={e.label} style={{
                boxSizing: 'border-box', flex: 1, minWidth: 20, height: 34, marginLeft: j ? -5 : 0,
                background: e.tint, borderLeft: '1px solid #D2D2D2', borderRight: '1px solid #D2D2D2',
                borderTop: `3px solid ${e.color}`, borderBottom: 'none', borderRadius: '7px 7px 0 0',
                boxShadow: '-2px 0 4px rgba(0,0,0,0.10)', display: 'flex', alignItems: 'flex-start',
                justifyContent: 'center', paddingTop: 5, position: 'relative', zIndex: 6 - j,
              }}>
                <span style={{
                  fontSize: 11, fontWeight: 700, color: matched ? MATCH_PAIR.fg : e.ink,
                  ...(matched ? { background: MATCH_PAIR.bg, borderRadius: 9, padding: '0 4px' } : null),
                }}>
                  {n > 0 ? n : '·'}
                </span>
              </span>
            );
          })}
        </span>
      </button>

      {open && (
        <div
          ref={listRef}
          id={listId}
          role="listbox"
          aria-label="Stages"
          onKeyDown={onListKey}
          style={{
            position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, zIndex: 100,
            background: '#FFFFFF', borderRadius: 12, boxShadow: '0 10px 26px rgba(0,0,0,0.22)', overflow: 'hidden',
          }}
        >
          {tabs.map(t => {
            const on = t.id === chosen.id;
            const n = counts[t.id] || 0;
            const pill = tabCountPill({ count: n, on, searching, ink: t.ink });
            return (
              <button
                key={t.id}
                type="button"
                role="option"
                aria-selected={on}
                // One stop in the tab order (the chosen stage); the arrow keys
                // move between the seven, and Tab leaves the list.
                tabIndex={on ? 0 : -1}
                data-stage-option={t.id}
                onClick={() => choose(t.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, width: '100%', minHeight: 44,
                  padding: '0 12px', borderTop: 'none', borderRight: 'none', borderBottom: '1px solid #F0F0F0',
                  borderLeft: `5px solid ${t.color}`, background: on ? t.tint : '#FFFFFF',
                  cursor: 'pointer', font: 'inherit', fontSize: 14, fontWeight: 600, textAlign: 'left',
                }}
              >
                <span style={{ flex: 1, color: t.ink }}>{t.label}</span>
                {pill && <CountPill pill={pill} n={n} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
